import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { JOURNEYS, TRAVELLER_META, FILTERS } from "../data/journeys";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";

export default function Journeys() {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();

    const traveller = params.get("traveller"); // adventure | culture | luxury | backpacker | null
    const stateF = params.get("state");
    const durationF = params.get("duration");
    const budgetF = params.get("budget");
    const seasonF = params.get("season");

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = traveller
            ? `${TRAVELLER_META[traveller]?.title || "Journeys"} — enei.in`
            : "Choose your traveller — enei.in";
    }, [traveller]);

    const meta = TRAVELLER_META[traveller];

    const filtered = useMemo(() => {
        if (!traveller) return [];
        return JOURNEYS.filter((j) => {
            if (j.traveller !== traveller) return false;
            if (stateF && j.state !== stateF) return false;
            if (durationF && j.durationBucket !== durationF) return false;
            if (budgetF && j.budgetBand !== budgetF) return false;
            if (seasonF && j.seasonBucket !== seasonF) return false;
            return true;
        });
    }, [traveller, stateF, durationF, budgetF, seasonF]);

    // Step 1: no traveller chosen → show identity picker
    if (!traveller || !meta) return <IdentityPicker onPick={(id) => setParams({ traveller: id })} />;

    const setFilter = (key, value) => {
        const next = new URLSearchParams(params);
        if (!value || next.get(key) === value) next.delete(key);
        else next.set(key, value);
        setParams(next);
    };

    const clearAll = () => setParams({ traveller });

    const activeCount = ["state", "duration", "budget", "season"].filter((k) => params.get(k)).length;

    return (
        <div className="App grain bg-paper">
            <Navbar />

            {/* Quiet contextual hero */}
            <section
                data-testid="journeys-hero"
                className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-stone"
            >
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-7">
                        <button
                            onClick={() => navigate("/journeys")}
                            data-testid="back-to-identity"
                            className="text-[11px] uppercase tracking-[0.22em] text-slatewarm hover:text-terracotta transition-colors mb-8"
                        >
                            ← Choose a different traveller
                        </button>
                        <p className="font-serif italic text-marigold text-xl md:text-2xl">
                            {meta.number} · {meta.title}
                        </p>
                        <h1 className="mt-4 font-serif text-forest text-4xl md:text-6xl lg:text-[80px] leading-[1.02] tracking-tight font-light max-w-3xl">
                            {meta.line}
                        </h1>
                        <p className="mt-8 text-slatewarm text-base md:text-[17px] leading-relaxed max-w-xl">
                            {meta.long}
                        </p>
                    </div>
                </div>
            </section>

            {/* Minimal filters */}
            <section className="bg-paper border-y border-forest/10 sticky top-[68px] z-30 backdrop-blur-md bg-paper/85">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-5 flex flex-wrap items-center gap-3 md:gap-5 text-[12px] uppercase tracking-[0.18em]">
                    <span className="text-slatewarm hidden md:inline">Filter —</span>
                    <FilterDropdown
                        label="State"
                        testId="filter-state"
                        options={FILTERS.states}
                        value={stateF}
                        onChange={(v) => setFilter("state", v)}
                    />
                    <FilterDropdown
                        label="Duration"
                        testId="filter-duration"
                        options={FILTERS.durations}
                        value={durationF}
                        onChange={(v) => setFilter("duration", v)}
                    />
                    <FilterDropdown
                        label="Budget"
                        testId="filter-budget"
                        options={FILTERS.budgets}
                        value={budgetF}
                        onChange={(v) => setFilter("budget", v)}
                    />
                    <FilterDropdown
                        label="Season"
                        testId="filter-season"
                        options={FILTERS.seasons}
                        value={seasonF}
                        onChange={(v) => setFilter("season", v)}
                    />
                    {activeCount > 0 && (
                        <button
                            onClick={clearAll}
                            data-testid="filter-clear"
                            className="ml-auto text-terracotta hover:text-forest transition-colors"
                        >
                            Clear filters
                        </button>
                    )}
                </div>
            </section>

            {/* Journey list — sparse, large, breathing */}
            <section className="py-20 md:py-28 bg-paper">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14">
                    <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-12">
                        — {filtered.length} {filtered.length === 1 ? "journey" : "journeys"} curated for you
                    </p>

                    {filtered.length === 0 ? (
                        <div data-testid="journeys-empty" className="py-20 text-center">
                            <p className="font-serif italic text-forest text-3xl md:text-4xl mb-6">
                                Nothing matches that, yet.
                            </p>
                            <p className="text-slatewarm max-w-md mx-auto mb-8">
                                We curate fewer journeys, more carefully. Try widening a filter — or
                                tell our curator what you're hoping for, and we'll build it for you.
                            </p>
                            <div className="flex justify-center gap-4">
                                <button
                                    onClick={clearAll}
                                    data-testid="empty-clear"
                                    className="text-[12px] uppercase tracking-[0.22em] text-forest border-b border-forest pb-1 hover:text-terracotta hover:border-terracotta"
                                >
                                    Clear filters
                                </button>
                                <Link
                                    to="/#plan"
                                    data-testid="empty-plan"
                                    className="bg-forest text-paper px-6 py-3 text-[12px] uppercase tracking-[0.22em] hover:bg-terracotta transition-all"
                                >
                                    Speak with a curator →
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-20 md:space-y-28">
                            {filtered.map((j, i) => (
                                <JourneyCard key={j.slug} j={j} flip={i % 2 === 1} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
}

const IdentityPicker = ({ onPick }) => {
    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "What kind of traveller are you, today? — enei.in";
    }, []);

    return (
        <div className="App grain bg-paper">
            <Navbar />
            <section
                data-testid="identity-picker"
                className="relative min-h-screen pt-32 md:pt-40 pb-20 bg-paper"
            >
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14">
                    <div className="max-w-4xl mb-16 md:mb-24">
                        <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-8">
                            — Begin
                        </p>
                        <h1 className="font-serif text-forest text-4xl md:text-6xl lg:text-[88px] leading-[1.02] tracking-tight font-light">
                            What kind of traveller are you, <em className="italic text-terracotta">today?</em>
                        </h1>
                        <p className="mt-10 text-slatewarm text-base md:text-[17px] leading-relaxed max-w-xl">
                            Often the same person is all four — at four different ages.
                            Pick the one that feels true this morning. You can always come back.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                        {Object.values(TRAVELLER_META).map((t) => (
                            <button
                                key={t.id}
                                onClick={() => onPick(t.id)}
                                data-testid={`identity-${t.id}`}
                                className="group relative h-[420px] md:h-[520px] overflow-hidden text-left bg-forest"
                            >
                                <img
                                    src={t.image}
                                    alt={t.title}
                                    loading="lazy"
                                    decoding="async"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-forest/95 via-forest/40 to-forest/10" />
                                <div className="relative z-10 h-full flex flex-col justify-between p-7 md:p-10">
                                    <span className="font-serif italic text-marigold text-2xl">
                                        {t.number}
                                    </span>
                                    <div>
                                        <h3 className="font-serif text-paper text-3xl md:text-5xl leading-[1.05] font-light">
                                            {t.title}
                                        </h3>
                                        <p className="mt-3 font-serif italic text-marigold text-lg md:text-xl">
                                            {t.line}
                                        </p>
                                        <p className="mt-5 text-paper/70 text-[11px] uppercase tracking-[0.22em] flex items-center gap-2">
                                            Begin <span aria-hidden>→</span>
                                        </p>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
};

const JourneyCard = ({ j, flip }) => (
    <Link
        to={`/journeys/${j.slug}`}
        data-testid={`journey-card-${j.slug}`}
        className="group grid grid-cols-12 gap-6 md:gap-12 items-center"
    >
        <div className={`col-span-12 md:col-span-7 ${flip ? "md:order-2" : ""}`}>
            <div className="relative aspect-[4/3] md:aspect-[5/4] overflow-hidden bg-stone">
                <img
                    src={j.image}
                    alt={j.title}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                />
            </div>
        </div>
        <div className={`col-span-12 md:col-span-5 ${flip ? "md:order-1" : ""}`}>
            <p className="text-[11px] uppercase tracking-[0.28em] text-terracotta mb-4">
                {FILTERS.states.find((s) => s.id === j.state)?.label} · {j.season}
            </p>
            <h2 className="font-serif text-forest text-3xl md:text-5xl leading-[1.05] font-light tracking-tight">
                {j.title}
            </h2>
            <p className="mt-5 font-serif italic text-slatewarm text-lg md:text-xl leading-snug max-w-md">
                {j.oneLiner}
            </p>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-slatewarm">
                <span>{j.duration}</span>
                <span className="text-forest/30">·</span>
                <span>{j.budgetLabel}</span>
            </div>
            <p className="mt-6 font-serif italic text-forest/60 text-base">
                {j.mood}
            </p>
            <span className="mt-8 inline-flex items-center gap-3 text-[12px] uppercase tracking-[0.22em] text-forest border-b border-forest/40 pb-1 group-hover:text-terracotta group-hover:border-terracotta transition-colors">
                Read the journey <span aria-hidden>→</span>
            </span>
        </div>
    </Link>
);

const FilterDropdown = ({ label, testId, options, value, onChange }) => {
    const active = options.find((o) => o.id === value);
    return (
        <div className="relative group">
            <button
                data-testid={testId}
                className={`px-4 py-2 border transition-all ${
                    active
                        ? "bg-forest text-paper border-forest"
                        : "bg-transparent text-forest border-forest/30 hover:border-terracotta hover:text-terracotta"
                }`}
            >
                {active ? active.label : label}
                <span className="ml-2 opacity-50">▾</span>
            </button>
            <div className="absolute top-full left-0 mt-1 min-w-[200px] bg-paper border border-forest/15 shadow-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-40">
                {options.map((o) => (
                    <button
                        key={o.id}
                        data-testid={`${testId}-${o.id}`}
                        onClick={() => onChange(o.id)}
                        className={`block w-full text-left px-4 py-3 text-[12px] uppercase tracking-[0.18em] hover:bg-stone ${
                            value === o.id ? "text-terracotta" : "text-forest"
                        }`}
                    >
                        {o.label}
                    </button>
                ))}
                {value && (
                    <button
                        onClick={() => onChange(null)}
                        data-testid={`${testId}-clear`}
                        className="block w-full text-left px-4 py-3 text-[11px] uppercase tracking-[0.18em] text-slatewarm border-t border-forest/10 hover:bg-stone"
                    >
                        Clear
                    </button>
                )}
            </div>
        </div>
    );
};
